import React from "react";
import { useUser } from '../../context/UserContext';
import BookIcon from '@mui/icons-material/MenuBook';
import InventoryIcon from '@mui/icons-material/Inventory';
import StarIcon from '@mui/icons-material/Star';
import { CardNumberElement, CardExpiryElement, CardCvcElement, IbanElement, useStripe, useElements, } from '@stripe/react-stripe-js';
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useState, useRef, useEffect } from 'react';
import ModalWrapper from "../../components/Modal/ModalWrapper";
// ...
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);
const AccessToken = localStorage.getItem("token");

type Plan = {
  name: string;
  headline?: string;
  description: string;
  features: string[];
  price_eur: string;
  price_gross_eur?: string;
  price_net_eur?: string;
  tax_rate?: string;
  tax_amount?: string;
  interval?: string;
  tier?: string;
  stripe_price_id: string;
  rank: number;
  color: string;
  icon_name: string;
};

type CheckoutStatus = "idle" | "processing" | "success" | "success_processing" | "updated" | "error";




// Icon mapping for plan icons
const iconMap: Record<string, JSX.Element> = {
  BookIcon: <BookIcon fontSize="small" className="text-white" />,
  InventoryIcon: <InventoryIcon fontSize="small" className="text-white" />,
  StarIcon: <StarIcon fontSize="small" className="text-white" />,
};


function shadeColor(color: string, percent: number) {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;
  return "#" + (
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  ).toString(16).slice(1);
}

const PlanPicker: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = React.useState<Plan | null>(null);
  const [showCheckoutForm, setShowCheckoutForm] = React.useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatus>("idle");
  const [checkoutMessage, setCheckoutMessage] = useState<string>("");
  const refreshOnceRef = useRef(false);
  const targetPlanRef = useRef<Plan | null>(null);
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const { user, loading, error: userError, updateUser, refreshUser } = useUser();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmed, setCancelConfirmed] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  type DiscountInfo = {
    code?: string | null;
    percent_off?: number;
    amount_off_eur?: number;
    duration_type?: string;
    duration_months?: number;
  };
  type DiscountCodeInfo = {
    id: number;
    code: string;
    discount: {
      name: string;
      percent_off?: string | number | null;
      amount_off_eur?: string | number | null;
      duration_type?: string | null;
      duration_months?: number | null;
      applies_to_all_plans?: boolean | null;
      eligible_intervals?: string[] | null;
      valid_to?: string | null;
    };
  };
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountInfo | null>(null);
  const [autoDiscount, setAutoDiscount] = useState<DiscountInfo | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const [personalCodes, setPersonalCodes] = useState<DiscountCodeInfo[]>([]);
  const [loadingPersonalCodes, setLoadingPersonalCodes] = useState(false);
  const [showAllCodes, setShowAllCodes] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState<{ amount_due: number | null; currency?: string | null } | null>(null);
  const [loadingInvoicePreview, setLoadingInvoicePreview] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState<"year" | "month">("month");
  const plansRef = useRef<HTMLDivElement | null>(null);
  const status = user?.subscription?.status ?? "";
  const planName = user?.subscription?.plan_name ?? (status === "free" || user?.subscription?.is_virtual_fallback ? "Free Plan" : "Kein Plan");
  const isProbeModel = /probe|demo|trial|test/i.test(planName);
  const planTier = user?.subscription?.plan_tier?.toLowerCase() || "";
  const trial14Match = /trial[\s_]*14/i.test(planTier) || /trial[\s-]*14/i.test(planName);
  const isPilot = planTier === "trial30";
  const isTrial14 = trial14Match;
  const canViewPlans = isTrial14 ? true : (user?.subscription?.can_view_plans ?? true);
  const canSubscribe = isTrial14 ? true : (user?.subscription?.can_subscribe ?? true);
  const shouldHidePlans = isPilot;
  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  useEffect(() => {
    targetPlanRef.current = selectedPlan;
  }, [selectedPlan]);
  const getCurrentPlan = () => {
    if (!user?.subscription) return null;
    const currentName = (user.subscription.plan_name ?? "").toLowerCase();
    const currentInterval = user.subscription.billing_interval;
    const exactMatch = plans.find(
      (plan) =>
        plan.name.toLowerCase() === currentName &&
        plan.interval === currentInterval
    );
    if (exactMatch) return exactMatch;
    return plans.find((plan) => plan.name.toLowerCase() === currentName) || null;
  };
  const getPendingPlanMeta = () => {
    if (!user?.subscription?.pending_plan) return null;
    const pending = user.subscription.pending_plan;
    return {
      name: (pending?.name ?? "").toLowerCase(),
      interval: pending?.interval,
      tier: (pending as { tier?: string } | null)?.tier,
      stripePriceId: (pending as { stripe_price_id?: string } | null)?.stripe_price_id,
    };
  };
  const getPendingPlan = (): Plan | null => {
    const pendingMeta = getPendingPlanMeta();
    if (!pendingMeta) return null;
    if (pendingMeta.stripePriceId) {
      return plans.find((plan) => plan.stripe_price_id === pendingMeta.stripePriceId) || null;
    }
    if (!pendingMeta.name) return null;
    return plans.find((plan) => plan.name.toLowerCase() === pendingMeta.name && plan.interval === pendingMeta.interval) || null;
  };
  const isPendingPlanMatch = (plan: Plan | null, pendingMeta: { name: string; interval?: string; tier?: string; stripePriceId?: string } | null) => {
    if (!plan || !pendingMeta) return false;
    if (pendingMeta.stripePriceId) {
      return plan.stripe_price_id === pendingMeta.stripePriceId;
    }
    if (pendingMeta.name && plan.name.toLowerCase() === pendingMeta.name) {
      return !pendingMeta.interval || plan.interval === pendingMeta.interval;
    }
    if (pendingMeta.tier && plan.tier && plan.tier.toLowerCase() === pendingMeta.tier.toLowerCase()) {
      return !pendingMeta.interval || plan.interval === pendingMeta.interval;
    }
    return false;
  };
  const getPlanChange = (plan: Plan | null) => {
    const currentPlan = getCurrentPlan();
    const pendingPlan = getPendingPlan();
    const pendingMeta = getPendingPlanMeta();
    const periodEndLabel = user?.subscription?.current_period_end
      ? new Date(user.subscription.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "";
    if (!plan || !currentPlan) {
      const isPendingPlan = isPendingPlanMatch(plan, pendingMeta);
      return { isCurrentPlan: false, isUpgrade: false, isDowngrade: false, isPendingPlan, periodEndLabel };
    }
    const sameInterval = currentPlan.interval ? plan.interval === currentPlan.interval : true;
    const isIntervalDowngrade = currentPlan.interval === "year" && plan.interval === "month";
    const isIntervalUpgrade = currentPlan.interval === "month" && plan.interval === "year";
    const isCurrentPlan = plan.name === currentPlan.name && plan.interval === currentPlan.interval;
    const isPendingPlan = isPendingPlanMatch(plan, pendingMeta);
    const isUpgrade = (sameInterval && plan.rank > currentPlan.rank) || isIntervalUpgrade;
    const isDowngrade = (sameInterval && plan.rank < currentPlan.rank) || isIntervalDowngrade;
    return { isCurrentPlan, isUpgrade, isDowngrade, isPendingPlan, periodEndLabel };
  };
  const handleIntervalToggle = (targetInterval: "year" | "month") => {
    setSelectedInterval(targetInterval);
    if (!selectedPlan) return;
    const matchingPlan = plans.find((plan) => {
      if (plan.interval !== targetInterval) return false;
      if (selectedPlan.tier && plan.tier) {
        return plan.tier === selectedPlan.tier;
      }
      return plan.name === selectedPlan.name;
    });
    if (matchingPlan) {
      setSelectedPlan(matchingPlan);
      setShowCheckoutForm(false);
    }
  };
  const handleCopyAndRedeem = async (code: string, interval?: "month" | "year") => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = code;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch (error) {
      console.error("Fehler beim Kopieren des Codes:", error);
    }
    if (interval) {
      setSelectedInterval(interval);
    }
    setDiscountCode(code);
    setDiscountError(null);
    setShowDiscountInput(true);
    scrollToPlans();
  };
  const handleCancelSubscription = async () => {
    try {
      const response = await fetch("/api/subscriptions/cancel-subscription/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();

      if (!response.ok || data.error) {
        setModal(
          <ModalWrapper onClose={() => {
            setModal(null);
            setShowCheckoutForm(false);
            setSelectedPlan(null);
            refreshUser();
          }} title="Fehler beim Abschluss">
            <p>
              Es gab einen Fehler bei der Buchung Ihres Abonnements. Bitte wenden Sie sich an unser <a href="https://lumanote.de/contact" target="_blank" rel="noreferrer" className="text-blue-600 underline">Support-Team</a>.
            </p>
            <div className="flex justify-center mt-6">
              <button
                onClick={() => {
                  setModal(null);
                  setShowCheckoutForm(false);
                  setSelectedPlan(null);
                  refreshUser();
                }}
                className="bg-primary-500 text-white rounded px-4 py-2 shadow hover:bg-primary-600"
              >
                OK
              </button>
            </div>
          </ModalWrapper>
        );
        console.error("Subscription creation failed:", data.error);
        return;
      }
      if (data.success) {
        alert("Ihr Abonnement wird zum Ende des Abrechnungszeitraums beendet.");
        await refreshUser();
      } else {
        alert(data.error || "Fehler bei der Kündigung.");
      }
      setShowCancelDialog(false);
      setCancelConfirmed(false);
    } catch (error) {
      console.error("Fehler bei der Kündigung:", error);
    }
  };

  useEffect(() => {
    const fetchAutoDiscount = async () => {
      if (!selectedPlan?.stripe_price_id) {
        return;
      }
      try {
        const response = await fetch(`/api/discounts/auto-apply/?priceId=${selectedPlan.stripe_price_id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.json();
        if (data.discount) {
          setAutoDiscount({
            code: data.discount.code ?? null,
            percent_off: data.discount.percent_off ? parseFloat(data.discount.percent_off) : undefined,
            amount_off_eur: data.discount.amount_off_eur ? parseFloat(data.discount.amount_off_eur) : undefined,
            duration_type: data.discount.duration_type,
            duration_months: data.discount.duration_months,
          });
        } else {
          setAutoDiscount(null);
        }
      } catch (error) {
        console.error("Fehler beim automatischen Rabatt:", error);
      }
    };
    fetchAutoDiscount();
  }, [selectedPlan?.stripe_price_id]);

  const handleDiscountApply = async () => {
    setDiscountError(null);
    if (!discountCode.trim()) {
      setDiscountError("Bitte einen Code eingeben.");
      return;
    }
    try {
      const response = await fetch("/api/discounts/validate/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          code: discountCode.trim(),
          priceId: selectedPlan?.stripe_price_id,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setDiscountError("Rabattcode ist nicht aktiv oder nicht gültig. Bitte prüfen Sie den Code oder kontaktieren Sie den Support.");
        setAppliedDiscount(null);
        return;
      }
      setAppliedDiscount({
        code: data.code || discountCode.trim(),
        percent_off: data.percent_off ? parseFloat(data.percent_off) : undefined,
        amount_off_eur: data.amount_off_eur ? parseFloat(data.amount_off_eur) : undefined,
        duration_type: data.duration_type,
        duration_months: data.duration_months,
      });
    } catch (error) {
      console.error("Fehler beim Prüfen des Codes:", error);
      setDiscountError("Code konnte nicht geprüft werden.");
    }
  };

  const formatCurrency = (value?: string | number) => {
    if (value === undefined || value === null || value === "") return "-";
    const parsed = typeof value === "number" ? value : parseFloat(`${value}`.replace(',', '.'));
    if (Number.isNaN(parsed)) return "-";
    return `${parsed.toFixed(2)}`.replace('.', ',') + " €";
  };
  const formatPlanPrice = (plan: Plan | null) => {
    if (!plan) return "";
    const value = plan.price_gross_eur ?? plan.price_eur;
    return formatCurrency(value);
  };
  const formatPriceValue = (value?: string | number) => {
    return formatCurrency(value);
  };
  const formatTaxRate = (plan: Plan | null) => {
    if (!plan?.tax_rate) return "-";
    const rate = parseFloat(`${plan.tax_rate}`);
    if (Number.isNaN(rate)) return "-";
    return `${Math.round(rate * 100)} %`;
  };
  const getGrossValue = (plan: Plan | null) => {
    if (!plan) return null;
    const raw = plan.price_gross_eur ?? plan.price_eur;
    const parsed = parseFloat(`${raw}`.replace(',', '.'));
    if (Number.isNaN(parsed)) return null;
    return parsed;
  };
  const getActiveDiscount = () => appliedDiscount ?? autoDiscount ?? null;
  const formatDiscountValue = (discount: DiscountInfo | null) => {
    if (!discount) return "";
    if (discount.percent_off !== undefined) {
      return `−${discount.percent_off} %`;
    }
    if (discount.amount_off_eur !== undefined) {
      return `−${formatCurrency(discount.amount_off_eur)}`;
    }
    return "";
  };
  const getDiscountDurationLabel = (discount: DiscountInfo | null, plan: Plan | null) => {
    if (!discount) return "";
    if (plan?.interval === "year") {
      return "Erstes Abrechnungsjahr";
    }
    if (discount.duration_type === "months" && discount.duration_months) {
      return `Erste ${discount.duration_months} ${discount.duration_months === 1 ? "Monat" : "Monate"}`;
    }
    if (discount.duration_type === "forever") {
      return "Dauerhafter Rabatt";
    }
    return "Einmaliger Rabatt";
  };
  const getDiscountDurationLabelSimple = (discount: DiscountInfo | null) => {
    if (!discount) return "";
    if (discount.duration_type === "months" && discount.duration_months) {
      return `Erste ${discount.duration_months} ${discount.duration_months === 1 ? "Monat" : "Monate"}`;
    }
    if (discount.duration_type === "forever") {
      return "Dauerhaft";
    }
    return "Einmalig";
  };
  const getDiscountedGrossValue = (gross: number | null, discount: DiscountInfo | null) => {
    if (gross === null || !discount) return null;
    if (discount.percent_off !== undefined) {
      return Math.max(0, gross * (1 - discount.percent_off / 100));
    }
    if (discount.amount_off_eur !== undefined) {
      return Math.max(0, gross - discount.amount_off_eur);
    }
    return gross;
  };
  const formatPreviewAmount = () => {
    if (!invoicePreview || invoicePreview.amount_due === null || invoicePreview.amount_due === undefined) {
      return null;
    }
    const value = invoicePreview.amount_due / 100;
    return formatCurrency(value);
  };

  const handleOpenStripePortal = async () => {
    try {
      const response = await fetch("/api/payments/create-portal-session/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || "Fehler beim Öffnen des Zahlungsportals.");
      }
    } catch (error) {
      console.error("Fehler beim Öffnen des Stripe-Portals:", error);
    }
  };

  const hasSyncedRef = useRef(false);
  useEffect(() => {
    const syncSubscription = async () => {
      if (!user || hasSyncedRef.current) {
        return;
      }
      const shouldSync =
        !user.subscription?.stripe_subscription_id ||
        user.subscription?.status === "pending" ||
        user.subscription?.status === "incomplete" ||
        user.subscription?.display_status === "error";
      if (!shouldSync) {
        return;
      }
      hasSyncedRef.current = true;
      if (!user.stripe_customer_id) {
        return;
      }
      try {
        await fetch("/api/subscriptions/sync-subscription/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        await refreshUser(); // danach Userdaten neu laden
      } catch (error) {
        console.error("Fehler bei der Synchronisierung mit Stripe:", error);
      }
    };

    syncSubscription();
  }, [user, refreshUser]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetch("/api/subscriptions/list-subscription-plans");
        const data = await response.json();
        setPlans(data);
      } catch (error) {
        console.error("Fehler beim Laden der Pläne:", error);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    const fetchInvoicePreview = async () => {
      if (!selectedPlan?.stripe_price_id) {
        setInvoicePreview(null);
        setInvoicePreviewError(false);
        return;
      }
      const { isUpgrade } = getPlanChange(selectedPlan);
      if (!showCheckoutForm && isUpgrade) {
        setInvoicePreview(null);
        setInvoicePreviewError(false);
        return;
      }
      setLoadingInvoicePreview(true);
      setInvoicePreviewError(false);
      try {
        const response = await fetch("/api/subscriptions/preview-invoice/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            priceId: selectedPlan.stripe_price_id,
            discountCode: appliedDiscount?.code || null,
          }),
        });
        const data = await response.json();
        if (response.ok && data.amount_due !== undefined && data.amount_due !== null) {
          setInvoicePreview({
            amount_due: data.amount_due,
            currency: data.currency ?? null,
          });
        } else {
          setInvoicePreview(null);
          setInvoicePreviewError(true);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Rechnungs-Vorschau:", error);
        setInvoicePreview(null);
        setInvoicePreviewError(true);
      } finally {
        setLoadingInvoicePreview(false);
      }
    };
    fetchInvoicePreview();
  }, [selectedPlan?.stripe_price_id, appliedDiscount?.code, autoDiscount, showCheckoutForm]);

  useEffect(() => {
    const fetchPersonalCodes = async () => {
      if (!user) return;
      setLoadingPersonalCodes(true);
      try {
        const response = await fetch("/api/discounts/my-codes/?personal_only=1", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const data = await response.json();
        setPersonalCodes(Array.isArray(data.codes) ? data.codes : []);
      } catch (error) {
        console.error("Fehler beim Laden der Gutscheine:", error);
      } finally {
        setLoadingPersonalCodes(false);
      }
    };
    fetchPersonalCodes();
  }, [user]);

  // Modal for unavailable booking
  const [modal, setModal] = React.useState<React.ReactNode | null>(null);
  function handlePlanSelect(plan: Plan) {
    if (!siteSettings || !siteSettings.allow_user_subscription) {
      setModal(
        <ModalWrapper onClose={() => setModal(null)} title="Buchung demnächst verfügbar">
          <p>
            Die Buchungsfunktion ist derzeit noch deaktiviert.
            Ab dem <strong>15. November</strong> können Sie reguläre Abo-Modelle buchen. {" "}
          </p>
          <p>
            Bis dahin steht Ihnen Ihr Testzugang uneingeschränkt zur Verfügung – vielen Dank für Ihre Geduld!
          </p>
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setModal(null)}
              className="bg-primary-500 text-white rounded px-4 py-2 shadow hover:bg-primary-600"
            >
              OK
            </button>
          </div>
        </ModalWrapper>
      );
      return;
    }
    if (!canSubscribe && !user?.is_admin_user && !user?.is_staff) {
      setModal(
        <ModalWrapper onClose={() => setModal(null)} title="Buchung derzeit nicht verfügbar">
          <p>
            Dieses Konto ist aktuell für einen Testzugang freigeschaltet. Eine Buchung regulärer Abo-Modelle ist derzeit nicht möglich.
          </p>
          <div className="flex justify-center mt-6">
            <button
              onClick={() => setModal(null)}
              className="bg-primary-500 text-white rounded px-4 py-2 shadow hover:bg-primary-600"
            >
              OK
            </button>
          </div>
        </ModalWrapper>
      );
      return;
    }
    const pendingMeta = getPendingPlanMeta();
    const isPendingTarget = isPendingPlanMatch(plan, pendingMeta);
    if (isPendingTarget) {
      return;
    }
    setCheckoutStatus("idle");
    setCheckoutMessage("");
    setSelectedPlan(plan);
    setShowCheckoutForm(false);
  }

  const closeCheckoutModal = async (options?: { skipRefresh?: boolean }) => {
    if (checkoutStatus === "processing") return;
    if (checkoutStatus === "error") {
      setCheckoutStatus("idle");
      setCheckoutMessage("");
      return;
    }
    setCheckoutStatus("idle");
    setCheckoutMessage("");
    setShowCheckoutForm(false);
    setSelectedPlan(null);
    if (!options?.skipRefresh) {
      try {
        await fetch("/api/subscriptions/sync-subscription/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } catch (error) {
        console.error("Fehler bei der Synchronisierung nach Checkout:", error);
      }
      await refreshUser({ fresh: true });
    }
  };

  const getCheckoutModalContent = () => {
    switch (checkoutStatus) {
      case "processing":
        return {
          title: "Zahlung wird verarbeitet...",
          body: "Bitte warten Sie einen Moment. Ihre Zahlung wird durchgeführt.",
          actionLabel: null,
        };
      case "updated":
        return {
          title: "Abo erfolgreich aktualisiert",
          body: "Ihr Abonnement wurde erfolgreich geändert. Die Abrechnung wurde automatisch übernommen.",
          actionLabel: "OK",
        };
      case "success_processing":
        return {
          title: "Zahlung eingereicht",
          body: "Ihre Zahlung wurde eingereicht. Ihr Abo wird aktiviert, sobald die Zahlung abgeschlossen ist. Sie erhalten eine Bestätigung per E-Mail.",
          actionLabel: "OK",
        };
      case "success":
        return {
          title: "Zahlung erfolgreich",
          body: "Ihre Zahlung war erfolgreich. Ihr Abo wird gerade aktiviert. Bitte warten Sie einen Moment – dieser Vorgang kann bis zu 1 Minute dauern.",
          actionLabel: "OK",
        };
      case "error":
        return {
          title: "Fehler beim Abschluss",
          body: checkoutMessage || "Es gab einen Fehler bei der Buchung. Bitte prüfen Sie Ihre Zahlungsdaten oder kontaktieren Sie den Support.",
          actionLabel: "OK",
        };
      default:
        return null;
    }
  };

  const checkoutModalContent = getCheckoutModalContent();
  const checkoutModal = checkoutModalContent ? (
    <ModalWrapper
      onClose={checkoutStatus === "processing" ? () => {} : closeCheckoutModal}
      title={checkoutModalContent.title}
    >
      <div className="flex flex-col items-center gap-4">
        {checkoutStatus === "processing" && (
          <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-[#32768d] animate-spin" />
        )}
        <p className="text-center">{checkoutModalContent.body}</p>
      </div>
      {checkoutModalContent.actionLabel && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => closeCheckoutModal()}
            className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
            style={{ backgroundColor: "#32768d" }}
          >
            {checkoutModalContent.actionLabel}
          </button>
        </div>
      )}
    </ModalWrapper>
  ) : null;

  useEffect(() => {
    if (!["success", "success_processing", "updated"].includes(checkoutStatus)) {
      refreshOnceRef.current = false;
      return;
    }
    if (refreshOnceRef.current) return;
    refreshOnceRef.current = true;
    const refreshOnce = async () => {
      try {
        await fetch("/api/subscriptions/sync-subscription/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      } catch (error) {
        console.error("Fehler bei der Hintergrund-Synchronisierung:", error);
      }
      await refreshUser();
    };
    refreshOnce();
  }, [checkoutStatus, refreshUser]);

  // Close only via user action to avoid auto-closing the modal.

  useEffect(() => {
    if (["success", "success_processing", "updated"].includes(checkoutStatus)) {
      setShowCheckoutForm(false);
    }
  }, [checkoutStatus]);


  // Stripe CheckoutForm Komponente
  const CheckoutForm = ({ user }: { user: any }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [paymentMethod, setPaymentMethod] = useState<"card" | "sepa_debit">("card");
    const [billingName, setBillingName] = useState(user?.full_name || user?.name || user?.email || "");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cardNumberError, setCardNumberError] = useState<string | null>(null);
    const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
    const [cardCvcError, setCardCvcError] = useState<string | null>(null);
    const [ibanError, setIbanError] = useState<string | null>(null);
    const showCheckoutFormFields = checkoutStatus === "idle" || checkoutStatus === "error";

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();
      if (!stripe || !elements || isSubmitting) return;
      setIsSubmitting(true);
      setCheckoutMessage("");

      let paymentMethodObj: any = null;
      let error: any = null;
      const buildIdempotencyKey = () => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        return `sub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      };
      const idempotencyKey = buildIdempotencyKey();

      if (paymentMethod === "card") {
        const cardElement = elements.getElement(CardNumberElement) as import('@stripe/stripe-js').StripeCardNumberElement | null;
        if (!cardElement) {
          setCheckoutStatus("error");
          setCheckoutMessage("Kartendaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.");
          setIsSubmitting(false);
          return;
        }
        const result = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: billingName || undefined,
            email: user?.email,
          },
        });
        error = result.error;
        paymentMethodObj = result.paymentMethod;
      } else if (paymentMethod === "sepa_debit") {
        const ibanElement = elements.getElement(IbanElement) as import('@stripe/stripe-js').StripeIbanElement | null;
        if (!ibanElement) {
          setCheckoutStatus("error");
          setCheckoutMessage("IBAN-Eingabe konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
          setIsSubmitting(false);
          return;
        }
        const result = await stripe.createPaymentMethod({
          type: 'sepa_debit',
          sepa_debit: ibanElement,
          billing_details: {
            name: billingName || user?.full_name || user?.email,
            email: user?.email,
          },
        });
        error = result.error;
        paymentMethodObj = result.paymentMethod;
      }

      if (error) {
        setCheckoutStatus("error");
        setCheckoutMessage(error.message || "Zahlungsmethode konnte nicht erstellt werden.");
        console.error(error);
        setIsSubmitting(false);
        return;
      }

      setCheckoutStatus("processing");

      // PaymentMethod created, send to backend
      const response = await fetch("/api/subscriptions/create-subscription/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethodObj.id,
          priceId: selectedPlan?.stripe_price_id,
          customerId: user?.stripe_customer_id || null,
          discountCode: appliedDiscount?.code || null,
          idempotencyKey,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        setCheckoutStatus("error");
        setCheckoutMessage(
          data.error ||
          "Es gab einen Fehler bei der Buchung Ihres Abonnements. Bitte wenden Sie sich an unser Support-Team."
        );
        console.error("Subscription creation failed:", data.error);
        setIsSubmitting(false);
        return;
      }

      // NEU: Stripe Customer ID im UserContext und in DB speichern, falls neu
      console.log("Subscription creation response:", data);
      if (data.customerId && data.customerId !== user?.stripe_customer_id) {
        await updateUser({ stripe_customer_id: data.customerId });
      }

      // NEU: ClientSecret-Check für automatische Up-/Downgrades ohne Zahlung
      if (!data.clientSecret) {
        console.log("⚠️ Kein ClientSecret vorhanden – wahrscheinlich automatisches Upgrade oder Downgrade ohne zusätzliche Zahlung.");
        setCheckoutStatus("updated");
        setIsSubmitting(false);
        return;
      }

      if (paymentMethod === "card") {
        const result = await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: paymentMethodObj.id,
        });
        const intent = result.paymentIntent || (result.error as import('@stripe/stripe-js').StripeError | undefined)?.payment_intent;
        const intentStatus = intent?.status;
        if (intentStatus === "succeeded") {
          setCheckoutStatus("success");
        } else if (intentStatus === "processing") {
          setCheckoutStatus("success_processing");
        } else if (result.error) {
          setCheckoutStatus("error");
          setCheckoutMessage(result.error.message || "Die Zahlung konnte nicht bestätigt werden.");
          console.error(result.error.message);
        } else {
          setCheckoutStatus("error");
          setCheckoutMessage("Die Zahlung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.");
        }
        setIsSubmitting(false);
      } else if (paymentMethod === "sepa_debit") {
        // SEPA-Lastschrift: confirm the payment if clientSecret is present
        if (data.clientSecret) {
          const result = await stripe.confirmSepaDebitPayment(data.clientSecret, {
            payment_method: paymentMethodObj.id,
          });
          const intent = result.paymentIntent || (result.error as import('@stripe/stripe-js').StripeError | undefined)?.payment_intent;
          const intentStatus = intent?.status;
          if (intentStatus === "processing" || intentStatus === "succeeded") {
            setCheckoutStatus("success_processing");
          } else if (result.error) {
            setCheckoutStatus("error");
            setCheckoutMessage(result.error.message || "Die SEPA-Lastschrift konnte nicht bestätigt werden.");
            console.error(result.error.message);
          } else {
            setCheckoutStatus("error");
            setCheckoutMessage("Die SEPA-Lastschrift konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.");
          }
          setIsSubmitting(false);
        } else {
          setCheckoutStatus("updated");
          setIsSubmitting(false);
        }
      }
    };

    return (
      <div className="w-full pt-6 pb-32 sm:pb-0 overflow-y-scroll scroll-hidden max-w-2xl mx-auto">
        {showCheckoutFormFields && (
          <form onSubmit={handleSubmit} className="-mt-8 space-y-4 w-full max-w-2xl mx-auto">
          <div className="space-y-4 w-full">
            {/* Zahlungsmethode Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Zahlungsmethode</label>
              <div className="-mt-4 flex w-full gap-3 flex-nowrap overflow-x-auto">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("card")}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${paymentMethod === "card"
                    ? "border-primary-500 text-primary-700 shadow-sm"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                >
                  <span
                    className={`h-3 w-3 shrink-0 rounded-full border ${paymentMethod === "card"
                      ? "border-black bg-black"
                      : "border-gray-300"
                      }`}
                  />
                  <span className="flex flex-col ">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                      <g>
                        <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#141413" strokeWidth="0" />
                        <path d="m27,7H5c-1.657,0-3,1.343-3,3v12c0,1.657,1.343,3,3,3h22c1.657,0,3-1.343,3-3v-12c0-1.657-1.343-3-3-3Zm2,15c0,1.103-.897,2-2,2H5c-1.103,0-2-.897-2-2v-12c0-1.103.897-2,2-2h22c1.103,0,2,.897,2,2v12Z" strokeWidth="0" opacity=".15" />
                        <path d="m27,8H5c-1.105,0-2,.895-2,2v1c0-1.105.895-2,2-2h22c1.105,0,2,.895,2,2v-1c0-1.105-.895-2-2-2Z" fill="#fff" opacity=".2" strokeWidth="0" />
                        <path fill="#ff5f00" strokeWidth="0" d="M13.597 11.677H18.407V20.32H13.597z" />
                        <path d="m13.902,15.999c0-1.68.779-3.283,2.092-4.322-2.382-1.878-5.849-1.466-7.727.932-1.863,2.382-1.451,5.833.947,7.712,2,1.573,4.795,1.573,6.795,0-1.329-1.038-2.107-2.642-2.107-4.322Z" fill="#eb001b" strokeWidth="0" />
                        <path d="m24.897,15.999c0,3.039-2.459,5.497-5.497,5.497-1.237,0-2.428-.412-3.39-1.176,2.382-1.878,2.795-5.329.916-7.727-.275-.336-.58-.657-.916-.916,2.382-1.878,5.849-1.466,7.712.932.764.962,1.176,2.153,1.176,3.39Z" fill="#f79e1b" strokeWidth="0" />
                      </g>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                      <g>
                        <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#fff" strokeWidth="0" />
                        <path d="m27,7H5c-1.657,0-3,1.343-3,3v12c0,1.657,1.343,3,3,3h22c1.657,0,3-1.343,3-3v-12c0-1.657-1.343-3-3-3Zm2,15c0,1.103-.897,2-2,2H5c-1.103,0-2-.897-2-2v-12c0-1.103.897-2,2-2h22c1.103,0,2,.897,2,2v12Z" strokeWidth="0" opacity=".15" />
                        <path d="m27,8H5c-1.105,0-2,.895-2,2v1c0-1.105.895-2,2-2h22c1.105,0,2,.895,2,2v-1c0-1.105-.895-2-2-2Z" fill="#fff" opacity=".2" strokeWidth="0" />
                        <path d="m13.392,12.624l-2.838,6.77h-1.851l-1.397-5.403c-.085-.332-.158-.454-.416-.595-.421-.229-1.117-.443-1.728-.576l.041-.196h2.98c.38,0,.721.253.808.69l.738,3.918,1.822-4.608h1.84Z" fill="#1434cb" strokeWidth="0" />
                        <path d="m20.646,17.183c.008-1.787-2.47-1.886-2.453-2.684.005-.243.237-.501.743-.567.251-.032.943-.058,1.727.303l.307-1.436c-.421-.152-.964-.299-1.638-.299-1.732,0-2.95.92-2.959,2.238-.011.975.87,1.518,1.533,1.843.683.332.912.545.909.841-.005.454-.545.655-1.047.663-.881.014-1.392-.238-1.799-.428l-.318,1.484c.41.188,1.165.351,1.947.359,1.841,0,3.044-.909,3.05-2.317" fill="#1434cb" strokeWidth="0" />
                        <path d="m25.423,12.624h-1.494c-.337,0-.62.195-.746.496l-2.628,6.274h1.839l.365-1.011h2.247l.212,1.011h1.62l-1.415-6.77Zm-2.16,4.372l.922-2.542.53,2.542h-1.452Z" fill="#1434cb" strokeWidth="0" />
                        <path fill="#1434cb" strokeWidth="0" d="M15.894 12.624L14.446 19.394 12.695 19.394 14.143 12.624 15.894 12.624z" />
                      </g>
                    </svg>
                  </span>
                  Kreditkarte
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("sepa_debit")}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${paymentMethod === "sepa_debit"
                    ? "border-primary-500 text-primary-700 shadow-sm"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                >
                  <span
                    className={`h-3 w-3 shrink-0 rounded-full border ${paymentMethod === "sepa_debit"
                      ? "border-black bg-black"
                      : "border-gray-300"
                      }`}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                    <g>
                      <rect x="2" y="7" width="28" height="18" rx="3" ry="3" fill="#e6e6e6" strokeWidth="0" />
                      <path d="m27,7H5c-1.657,0-3,1.343-3,3v12c0,1.657,1.343,3,3,3h22c1.657,0,3-1.343,3-3v-12c0-1.657-1.343-3-3-3Zm2,15c0,1.103-.897,2-2,2H5c-1.103,0-2-.897-2-2v-12c0-1.103.897-2,2-2h22c1.103,0,2,.897,2,2v12Z" opacity=".15" strokeWidth="0" />
                      <path d="m27,8H5c-1.105,0-2,.895-2,2v1c0-1.105.895-2,2-2h22c1.105,0,2,.895,2,2v-1c0-1.105-.895-2-2-2Z" fill="#fff" opacity=".2" strokeWidth="0" />
                      <path d="m21.5,21h-11c-.2764,0-.5.2236-.5.5s.2236.5.5.5h11c.2764,0,.5-.2236.5-.5s-.2236-.5-.5-.5Z" fill="#1a1a1a" opacity=".5" strokeWidth="0" />
                      <path d="m21.6694,13.6787l-4.8486-3.2324c-.499-.332-1.1426-.332-1.6416,0l-4.8491,3.2324c-.2661.1777-.3823.5029-.2896.8096.0928.3062.3701.5117.6899.5117h1.0195v4h-.75c-.2764,0-.5.2236-.5.5s.2236.5.5.5h10c.2764,0,.5-.2236.5-.5s-.2236-.5-.5-.5h-.75v-4h1.0195c.3198,0,.5972-.2056.6899-.5117.0928-.3066-.0234-.6318-.29-.8096Zm-7.4194,5.3213h-1.5v-4h1.5v4Zm2.5,0h-1.5v-4h1.5v4Zm-.75-5.5c-.4142,0-.75-.3358-.75-.75s.3358-.75.75-.75.75.3358.75.75-.3358.75-.75.75Zm3.25,5.5h-1.5v-4h1.5v4Z" fill="#1a1a1a" opacity=".5" strokeWidth="0" />
                    </g>
                  </svg>
                  SEPA-Lastschrift
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {paymentMethod === "card" ? "Name des Kreditkarteninhabers" : "Name des Kontoinhabers"}
              </label>
              <div className="border p-3 rounded">
                <input
                  type="text"
                  className="w-full bg-transparent outline-none text-[#32325d] text-[16px] placeholder-[#aab7c4]"
                  value={billingName}
                  onChange={(e) => setBillingName(e.target.value)}
                  placeholder="Max Mustermann"
                  autoComplete="name"
                  style={{ fontFamily: "Arial, sans-serif" }}
                />
              </div>
            </div>
            {/* Kreditkarte Felder */}
            {paymentMethod === "card" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Kartennummer</label>
                  <div className="border p-3 rounded">
                    <CardNumberElement
                      options={{
                        style: {
                          base: {
                            fontSize: "16px",
                            color: "#32325d",
                            fontFamily: "Arial, sans-serif",
                            '::placeholder': {
                              color: "#aab7c4",
                            },
                          },
                          invalid: {
                            color: "#fa755a",
                            iconColor: "#fa755a",
                          },
                        },
                      }}
                      onChange={(event) => setCardNumberError(event.error?.message || null)}
                    />
                  </div>
                  {cardNumberError && (
                    <p className="mt-1 text-sm text-red-600">{cardNumberError}</p>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700">Ablaufdatum</label>
                    <div className="border p-3 rounded">
                      <CardExpiryElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#32325d",
                              fontFamily: "Arial, sans-serif",
                              '::placeholder': {
                                color: "#aab7c4",
                              },
                            },
                            invalid: {
                              color: "#fa755a",
                              iconColor: "#fa755a",
                            },
                          },
                        }}
                        onChange={(event) => setCardExpiryError(event.error?.message || null)}
                      />
                    </div>
                    {cardExpiryError && (
                      <p className="mt-1 text-sm text-red-600">{cardExpiryError}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-gray-700">CVC</label>
                    <div className="border p-3 rounded">
                      <CardCvcElement
                        options={{
                          style: {
                            base: {
                              fontSize: "16px",
                              color: "#32325d",
                              fontFamily: "Arial, sans-serif",
                              '::placeholder': {
                                color: "#aab7c4",
                              },
                            },
                            invalid: {
                              color: "#fa755a",
                              iconColor: "#fa755a",
                          },
                        },
                      }}
                      onChange={(event) => setCardCvcError(event.error?.message || null)}
                    />
                  </div>
                  {cardCvcError && (
                    <p className="mt-1 text-sm text-red-600">{cardCvcError}</p>
                  )}
                </div>

                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Mit der Angabe Ihrer Kartendaten erklären Sie sich damit einverstanden, dass LumaNote GbR. Ihre Karte für zukünftige Zahlungen gemäß seinen Bedingungen belastet.                </div>
              </>
            )}
            {/* SEPA Felder */}
            {paymentMethod === "sepa_debit" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">IBAN</label>
                <div className="border p-3 rounded">
                  <IbanElement
                    options={{
                      supportedCountries: ['SEPA'],
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#32325d",
                          fontFamily: "Arial, sans-serif",
                          '::placeholder': {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#fa755a",
                          iconColor: "#fa755a",
                        },
                      },
                    }}
                    onChange={(event) => setIbanError(event.error?.message || null)}
                  />
                </div>
                {ibanError && (
                  <p className="mt-1 text-sm text-red-600">{ibanError}</p>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  Mit der Angabe Ihrer IBAN erteilen Sie LumaNote GbR ein SEPA-Lastschriftmandat. Sie werden vor der Belastung per E-Mail informiert.
                </div>
              </div>
            )}
          </div>
          {/* Begin: Subscribe/confirmation UI from payment-form__powered */}
          <div className="payment-form__powered">
            <button data-test-id="checkout-subscribe-btn" type="submit" id="" className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !font-normal"
              style={{
                backgroundColor: "#32768d",
                color: "white",
                border: "none"
              }} disabled={!stripe || isSubmitting}>
              <div className="_btnFlex_1uoax_32">{isSubmitting ? "Bitte warten..." : "Verbindlich buchen"}</div>
            </button>
            <div className="flex flex-col items-center justify-center font-bold mt-2 text-sm text-gray-500">
              <span>
                {(() => {
                  const { isUpgrade, isDowngrade, periodEndLabel } = getPlanChange(selectedPlan);
                  if (isDowngrade) {
                    return (
                      <>
                        Der neue Tarif startet {periodEndLabel ? `am ${periodEndLabel}` : "zum Ende der aktuellen Abrechnungsperiode"}.
                        {" "}Heute erfolgt keine Abbuchung.
                      </>
                    );
                  }
                  if (isUpgrade) {
                    const previewAmount = formatPreviewAmount();
                    if (previewAmount) {
                      return (
                        <>
                          Gesamtbetrag von {previewAmount} (inkl. MwSt.)
                        </>
                      );
                    }
                    if (loadingInvoicePreview) {
                      return <>Betrag wird berechnet...</>;
                    }
                    if (invoicePreviewError) {
                      return <>Betrag konnte nicht berechnet werden.</>;
                    }
                    return <>Betrag wird berechnet...</>;
                  }
                  const previewAmount = formatPreviewAmount();
                  if (previewAmount) {
                    return <>Gesamtbetrag von {previewAmount} (inkl. MwSt.)</>;
                  }
                  const activeDiscount = getActiveDiscount();
                  const gross = getGrossValue(selectedPlan);
                  const discountedGross = getDiscountedGrossValue(gross, activeDiscount);
                  return (
                    <>
                      Gesamtbetrag von{" "}
                      {activeDiscount && discountedGross !== null
                        ? formatCurrency(discountedGross)
                        : formatPlanPrice(selectedPlan)}{" "}
                      (inkl. MwSt.)
                    </>
                  );
                })()}
              </span>
              <span> Verlängert sich automatisch</span>
            </div>
            <div className="flex flex-col gap-2 pt-3 w-full">
              <div className="text-[#20A961] gap-1 flex items-center justify-center w-full">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame"><path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M8 1C7.07174 1 6.1815 1.36875 5.52513 2.02513C4.86875 2.6815 4.5 3.57174 4.5 4.5V6.5C3.96957 6.5 3.46086 6.71071 3.08579 7.08579C2.71071 7.46086 2.5 7.96957 2.5 8.5V13C2.5 13.5304 2.71071 14.0391 3.08579 14.4142C3.46086 14.7893 3.96957 15 4.5 15H11.5C12.0304 15 12.5391 14.7893 12.9142 14.4142C13.2893 14.0391 13.5 13.5304 13.5 13V8.5C13.5 7.96957 13.2893 7.46086 12.9142 7.08579C12.5391 6.71071 12.0304 6.5 11.5 6.5V4.5C11.5 2.56667 9.93333 1 8 1ZM10.5 6.5V4.5C10.5 3.83696 10.2366 3.20107 9.76777 2.73223C9.29893 2.26339 8.66304 2 8 2C7.33696 2 6.70107 2.26339 6.23223 2.73223C5.76339 3.20107 5.5 3.83696 5.5 4.5V6.5H10.5Z" fill="#20A961"></path></g></svg>
                <span className="font-bold text-base"> Sicherer checkout</span>
              </div>
              <div className="flex items-center justify-center w-full">
                <p className="text-black m-0">
                  <span className="-mr-2 text-md font-normal">Powered by</span>
                  <svg fill="#0e0e0e" fillRule="evenodd" height="36" width="72" viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg">
                    
                    <path d="m101.547 30.94c0-5.885-2.85-10.53-8.3-10.53-5.47 0-8.782 4.644-8.782 10.483 0 6.92 3.908 10.414 9.517 10.414 2.736 0 4.805-.62 6.368-1.494v-4.598c-1.563.782-3.356 1.264-5.632 1.264-2.23 0-4.207-.782-4.46-3.494h11.24c0-.3.046-1.494.046-2.046zm-11.347-2.183c0-2.598 1.586-3.678 3.035-3.678 1.402 0 2.897 1.08 2.897 3.678zm-14.597-8.345c-2.253 0-3.7 1.057-4.506 1.793l-.3-1.425h-5.067v26.805l5.747-1.218.023-6.506c.828.598 2.046 1.448 4.07 1.448 4.115 0 7.862-3.3 7.862-10.598-.023-6.667-3.816-10.3-7.84-10.3zm-1.38 15.84c-1.356 0-2.16-.483-2.713-1.08l-.023-8.53c.598-.667 1.425-1.126 2.736-1.126 2.092 0 3.54 2.345 3.54 5.356 0 3.08-1.425 5.38-3.54 5.38zm-16.4-17.196 5.77-1.24v-4.666l-5.77 1.218zm0 1.747h5.77v20.115h-5.77zm-6.185 1.7-.368-1.7h-4.966v20.117h5.747v-13.634c1.356-1.77 3.655-1.448 4.368-1.195v-5.287c-.736-.276-3.425-.782-4.782 1.7zm-11.494-6.7-5.609 1.197-.023 18.414c0 3.402 2.552 5.908 5.954 5.908 1.885 0 3.264-.345 4.023-.76v-4.667c-.736.3-4.368 1.356-4.368-2.046v-8.149h4.368v-4.897h-4.37zm-15.54 10.828c0-.897.736-1.24 1.954-1.24a12.85 12.85 0 0 1 5.7 1.47v-5.391c-1.908-.76-3.793-1.057-5.7-1.057-4.667 0-7.77 2.437-7.77 6.506 0 6.345 8.736 5.333 8.736 8.07 0 1.057-.92 1.402-2.207 1.402-1.908 0-4.345-.782-6.276-1.84v5.47c2.138.92 4.3 1.3 6.276 1.3 4.782 0 8.07-2.368 8.07-6.483-.023-6.85-8.782-5.632-8.782-8.207z" />
                  </svg>
                  <span className="font-normal"> - </span>
                  <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="https://www.lumanoteEX.com/terms-of-use" target="_blank" rel="noreferrer">Nutzungsbedingungen</a></span>
                  <span className="font-normal"> &amp; </span>
                  <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="https://www.lumanoteEX.com/privacy-policy" target="_blank" rel="noreferrer">Datenschutz</a></span>
                </p>
              </div>
            </div>

          </div>
          {/* End: Subscribe/confirmation UI */}
          </form>
        )}
      </div>
    );
  };

  // Ladezustand
  if ((loading || loadingPlans) && checkoutStatus === "idle") return <div className="p-6">Lade...</div>;
  if (!user && checkoutStatus === "idle") {
    return (
      <div className="p-6 space-y-3">
        <p>{userError || "Lade Benutzer..."}</p>
        {userError && (
          <button
            type="button"
            onClick={() => refreshUser({ fresh: true })}
            className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
            style={{ backgroundColor: "#32768d" }}
          >
            Erneut versuchen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="w-full pt-2 lg:pt-2 pb-32 sm:pb-0 overflow-y-auto overflow-x-auto scrollbar-hide h-[calc(100vh-10rem)] px-4">
      {modal}
      {checkoutModal}
      {/* Abonnementstatus anzeigen */}

      {(() => {


        const periodStart = user?.subscription?.current_period_start
          ? new Date(user.subscription.current_period_start).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        const periodEnd = user?.subscription?.current_period_end
          ? new Date(user.subscription.current_period_end).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        const startDate = user?.subscription?.start_date
          ? new Date(user.subscription.start_date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "-";
        // Statusanzeige und Farben
        let title = "";
        let statusText = "";
        let statusColor = "text-gray-600";
        let details = "";
        let extraDetails: React.ReactNode = null;
        let extraButtons: React.ReactNode = null;
        let showCancel = false;

        if (["pending", "incomplete"].includes(status)) {
          title = "Zahlung wird verarbeitet";
          statusText = "Status: Zahlung wird verarbeitet";
          statusColor = "text-yellow-600";
          details = `Beginn: ${startDate}\nAktuelle Periode: ${periodStart} – ${periodEnd}\nPreis: ${user?.subscription?.price_display ?? "-"}`;
          extraDetails = (
            <p className="text-sm text-yellow-700">
              Ihre Buchung wurde empfangen und wird derzeit verarbeitet. Sobald die Zahlung bestätigt ist, wird Ihr Abo automatisch aktiviert.
            </p>
          );
        } else if (status === "trialing") {
          title = "Probe-Abo aktiv";
          statusText = user?.subscription?.current_period_end
            ? `Ihr Probe-Abo läuft bis ${periodEnd}.`
            : "Probe-Abo aktiv";
          statusColor = "text-green-600";
          details = "Dieses Probe-Abo endet automatisch und verlängert sich nicht.";
        } else if (status === "staff") {
          title = "Mitarbeiterkonto aktiv";
          statusText = "Sie haben unbegrenzten Zugriff auf alle Funktionen.";
          statusColor = "text-green-600";
          details = "";
        } else if (status === "free" || user?.subscription?.is_virtual_fallback) {
          title = "Kostenloser Plan aktiv";
          statusText = "Status: Aktiv (kostenlos)";
          statusColor = "text-green-600";
          details = "";
          extraDetails = (
            <>
              <p className="text-sm text-gray-500 mb-1">
                Sie nutzen derzeit unseren kostenlosen Basiszugang mit eingeschränkten Funktionen.
              </p>
              <p className="text-sm text-gray-500">
                Sie können jederzeit ein{" "}
                <button
                  type="button"
                  onClick={scrollToPlans}
                  className="text-primary-600 underline hover:text-primary-800"
                >
                  höheres Abo-Modell
                </button>{" "}
                buchen, um zusätzliche Funktionen freizuschalten.
              </p>
            </>
          );
        } else if (status === "active") {
          title = "Aktives Abonnement";
          statusText = "Status: Aktiv";
          statusColor = "text-green-700";
          details = `Beginn: ${startDate}\nAktuelle Periode: ${periodStart} – ${periodEnd}\nZahlungsintervall: ${user?.subscription?.billing_interval === "year" ? "jährlich" : "monatlich"}\nPreis: ${user?.subscription?.price_display ?? "-"}`;
          showCancel = true;
          // Kündigungs-/Wechselabschnitt harmonisch gestalten:
          extraDetails = (
            <>
              {details && (
                <p className="text-sm text-gray-500 mb-1" style={{ whiteSpace: "pre-line" }}>{details}</p>
              )}
              <p className="text-sm text-gray-500 mb-1">
                Sie können Ihr Abo jederzeit erweitern oder auf ein anderes Modell wechseln –{" "}
                <button
                  type="button"
                  onClick={scrollToPlans}
                  className="text-primary-600 underline hover:text-primary-800"
                >
                  hier klicken
                </button>.
              </p>
              {user?.subscription?.cancellation_pending ? (
                <p className="mt-2 text-sm text-gray-600">
                  Ihre Kündigung ist vorgemerkt. Das Abonnement läuft noch bis {periodEnd} weiter.
                </p>
              ) : (
                <>

                  <button
                    type="button"
                    onClick={() => setShowCancelDialog(true)}
                    className="text-red-600 font-semibold mt-1 hover:underline text-left"
                  >
                    Abo kündigen
                  </button>
                </>
              )}
            </>
          );
        } else if (["past_due", "unpaid"].includes(status)) {
          title = "Zahlung ausstehend";
          statusText = "Ihre Zahlung ist überfällig oder fehlgeschlagen. Bitte prüfen Sie Ihre Zahlungsdaten.";
          statusColor = "text-yellow-800";
          details = "";
          extraButtons = (
            <button
              type="button"
              onClick={handleOpenStripePortal}
              className="_button_19rto_40 _primary_19rto_127 _ghost_19rto_151 text-base mt-2"
            >
              Zahlungsdaten aktualisieren
            </button>
          );
        } else if (status === "canceled") {
          title = "Abo beendet";
          statusText = `Ihr Abonnement wurde gekündigt und endet am ${periodEnd}.`;
          statusColor = "text-gray-600";
          details = "";
        } else if (status === "paused") {
          title = "Abo pausiert";
          statusText = "Ihr Abo ist derzeit pausiert. Bitte wenden Sie sich an den Support, um es wieder zu aktivieren.";
          statusColor = "text-gray-700";
          details = "";
        } else if (status === "pending_update") {
          title = "Abo wird aktualisiert";
          statusText = "Ihr Abonnement wird gerade geändert. Bitte warten Sie einen Moment.";
          statusColor = "text-blue-700";
          details = "";
        } else if (status === "incomplete_expired") {
          title = "Abo abgelaufen";
          statusText = "Ihre Buchung wurde nie abgeschlossen. Bitte wählen Sie ein neues Paket.";
          statusColor = "text-red-700";
          details = "";
        } else {
          // Fallback: kein Status
          return null;
        }

        return (
          <div className="flex flex-col gap-1 my-5 text-left bg-white border border-gray-200 rounded-md p-4 shadow-sm">
            <h2 className="text-lg font-bold mb-2">{title}</h2>
            {isProbeModel ? (
              <>
                <p className="text-sm text-gray-700 mb-1">Probe-Abo: {planName}</p>
                <p className="text-sm text-green-600 font-semibold">
                  Ihre Testphase läuft seit {periodStart} und endet am {periodEnd}.
                </p>
                <p className="text-sm text-gray-500 mb-1">
                  {isTrial14 ? (
                    "Dieses Testabonnement ermöglicht es Ihnen, LumaNote unverbindlich kennenzulernen. Die Testphase ist zeitlich befristet und endet automatisch. Es entstehen keine Kosten. Nach Ablauf der Testphase wird Ihr Zugang als kostenfreier Basiszugang fortgeführt, sofern kein kostenpflichtiges Abonnement gebucht wird."
                  ) : (
                    <>
                      Dieses Probe-Abo dient zur Erprobung unserer Funktionen und endet automatisch, ohne dass Kosten entstehen oder eine Verlängerung erfolgt. Während der Testphase ist eine Buchung regulärer Abo-Modelle noch nicht möglich. Weitere Informationen zu unseren Preisen finden Sie unter{" "}
                      <a
                        href="https://lumanote.de/pricing"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 underline hover:text-primary-800"
                      >
                        lumanote.de/pricing
                      </a>
                      . Bei Interesse können Sie sich jederzeit gerne vorab an uns wenden.
                    </>
                  )}
                </p>
                {!isPilot && (

                  <p className="text-sm text-gray-500">
                    Sie können jederzeit ein {" "}
                    <button
                      type="button"
                      onClick={scrollToPlans}
                      className="text-primary-600 underline hover:text-primary-800"
                    >
                      reguläres Abo-Modell
                    </button>
                    {" "} buchen, wenn Sie mit LumaNote zufrieden sind.
                  </p>)}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-1">Abo-Modell: {planName}</p>
                <p className={`text-sm mb-1 font-semibold ${statusColor}`}>{statusText}</p>
                {user?.subscription?.pending_plan && user?.subscription?.pending_change_at && (
                  <p className="text-sm text-gray-600 mb-1">
                    Geplanter Wechsel: {user.subscription.pending_plan.name} ab{" "}
                    {new Date(user.subscription.pending_change_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}.
                    {" "}Ihr aktueller Tarif bleibt bis dahin unverändert.
                  </p>
                )}
                {extraDetails}
                {extraButtons}
              </>
            )}
          </div>
        );
      })()}
      {(loadingPersonalCodes || personalCodes.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm my-5 flex flex-col gap-1">
          <h2 className="text-lg font-bold mb-2">Persönlicher Gutscheine</h2>
          <p className="text-sm text-gray-500 mb-2">
            Wählen Sie einen Gutschein und anschließend den gewünschten Tarif.
            Der Preisvorteil wird im Checkout automatisch berücksichtigt.
            Ein Klick kopiert den Gutscheincode.
          </p>
          {loadingPersonalCodes ? (
            <p className="text-sm text-gray-500 mb-1">Lade Gutscheine...</p>
          ) : (
            (showAllCodes ? personalCodes : personalCodes.slice(0, 1)).map((code) => {
              const discountInfo: DiscountInfo = {
                percent_off: code.discount?.percent_off ? Number(code.discount.percent_off) : undefined,
                amount_off_eur: code.discount?.amount_off_eur ? Number(code.discount.amount_off_eur) : undefined,
                duration_type: code.discount?.duration_type ?? undefined,
                duration_months: code.discount?.duration_months ?? undefined,
              };
              const validToLabel = code.discount?.valid_to
                ? new Date(code.discount.valid_to).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
                : "-";
              const eligibleIntervals = code.discount?.eligible_intervals ?? [];
              const isPlanLimited = code.discount?.applies_to_all_plans === false;
              const targetInterval =
                isPlanLimited && eligibleIntervals.length === 1
                  ? (eligibleIntervals[0] as "month" | "year")
                  : undefined;
              return (
                <div key={code.id} className="flex items-start justify-between gap-4 py-3 border-t border-gray-100 first:border-t-0 first:pt-0">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 w-full">
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-semibold">Code:</span> {code.code}
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-semibold">Rabatt:</span>{" "}
                      {formatDiscountValue(discountInfo) || "Individueller Vorteil"}
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="font-semibold">Laufzeit:</span>{" "}
                      {getDiscountDurationLabelSimple(discountInfo)}
                    </p>
                    {isPlanLimited && eligibleIntervals.length > 0 && (
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-semibold">Gültig für:</span>{" "}
                        {eligibleIntervals.includes("year") ? "jährlich" : "monatlich"}
                      </p>
                    )}
                    {code.discount?.name && (
                      <p className="text-sm text-gray-500 mb-1">
                        <span className="font-semibold">Bezeichnung:</span> {code.discount.name}
                      </p>
                    )}
                    <p className="text-sm text-gray-500 mb-1">
                      <span className="font-semibold">Gültig bis:</span> {validToLabel}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopyAndRedeem(code.code, targetInterval)}
                      className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 text-sm"
                      style={{
                        backgroundColor: "#ebb64e",
                        color: "white",
                        border: "none"
                      }}
                    >
                      <div className="_btnFlex_19rto_32">Kopieren und einlösen</div>
                    </button>
                  </div>
                </div>
              );
            })
          )}
          {!loadingPersonalCodes && personalCodes.length > 1 && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowAllCodes((prev) => !prev)}
                className="text-primary-600 text-sm font-medium hover:underline"
              >
                {showAllCodes ? "Weniger anzeigen" : "Weitere anzeigen"}
              </button>
            </div>
          )}
        </div>
      )}
      {user?.stripe_customer_id && (
        <div className="bg-white border border-gray-200 rounded-md p-4 shadow-sm my-5 flex flex-col gap-1">
          <h2 className="text-lg font-bold mb-2">Abonnement verwalten oder ändern</h2>
          <p className="text-sm text-gray-700 mb-1">Rechnungen herunterladen oder Zahlungsinformationen aktualisieren</p>
          <div className="flex flex-col md:flex-row w-full justify-between">
            <p className="text-gray-500 lg:w-[70%] w-full text-sm">
              Sie möchten eine Rechnung herunterladen oder Ihre Zahlungsdaten anpassen? Wir nutzen Stripe für die sichere Zahlungsabwicklung. Klicken Sie auf „Abrechnung verwalten“, um fortzufahren.
            </p>
            <button
              type="button"
              onClick={handleOpenStripePortal}
              className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
              style={{
                backgroundColor: "#40937a",
                color: "white",
                border: "none"
              }}
            >
              <div className="_btnFlex_19rto_32 text-sm">Zahlungsdetails verwalten</div>
            </button>
          </div>
        </div>
      )}
      {!shouldHidePlans && canViewPlans && (

        <div className="flex items-start">
          <div className="payment">
            {selectedPlan ? (
              showCheckoutForm ? (
                <div className="payment__portal__checkout">
                  <div id="payment-form" data-testid="payment-form">
                    <div className="flex gap-6 w-full">
                      <img
                        src="/logo_only.png"
                        className="block h-[80px] w-[80px]"
                        alt="Mobiles Logo"
                      />
                      <div className="flex flex-col gap-1">
                        <p className="text-[#1A1A1A99] text-base m-0">Sie abonnieren Sie das {selectedPlan?.name}</p>
                        <p className="plans-info flex flex-col">
                          <span className="text-black font-extrabold text-2xl">{formatPlanPrice(selectedPlan)} </span>
                          <span className="text-[#1A1A1A99] text-sm">
                            {selectedPlan?.interval === "year" ? " /jährlich abgerechnet" : " / monatlich abgerechnet"}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="border border-solid border-gray-200 p-4 rounded-lg min-h-[300px]">
                      <p className="text-black font-bold text-base">Zahlungsdaten</p>
                      <div className="StripeElement">
                        <Elements key="checkout" stripe={stripePromise}>
                          <CheckoutForm user={user} />
                        </Elements>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // keep existing original summary block here
                <div className="payment__portal__create">
                  <div className="flex gap-6 w-full">
                    <img
                      src="/logo_only.png"
                      className="block h-[80px] w-[80px]"
                      alt="Mobiles Logo"
                    />
                    <div className="flex flex-col gap-1">
                      <p className="text-[#1A1A1A99] text-base m-0">Abonnieren Sie das {selectedPlan?.name}</p>
                      <p className="plans-info flex flex-col">
                        <span className="text-black font-extrabold text-2xl">{formatPlanPrice(selectedPlan)} </span>
                        <span className="text-[#1A1A1A99] text-sm">
                          {selectedPlan?.interval === "year" ? " /jährlich abgerechnet " : " / monatlich abgerechnet "}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex sm:gap-10 gap-4 my-2 pt-4 w-full" style={{ borderTop: "1px solid rgb(229, 229, 229)" }}>
                    <p className="flex items-center text-md text-base font-extrabold m-0">{selectedPlan?.name.toUpperCase()}</p>
                    <div className="flex gap-2 text-nowrap">
                      <div className="subscription-toggle year-active">
                        <div className="subscription-toggle-inner yearly-active"></div>
                      </div>
                      <p className="flex items-center bg-[#D5F6DE] m-0  rounded-md px-1">17 % sparen</p>
                      <p className="flex items-center m-0 text-gray-500"> beijährlicher Zahlung</p>
                    </div>
                  </div>
                  <p className="m-0 py-2 text-gray-500 font-medium pb-4" style={{ borderBottom: "1px solid rgb(229, 229, 229)" }}>{selectedPlan?.description}</p>
                  <div className="payment__portal__create__sub__total">
                    <div className="sub_total">
                      <p className="m-0">Nettopreis</p>
                      <p className="m-0">{formatPriceValue(selectedPlan?.price_net_eur ?? selectedPlan?.price_eur)}</p>
                    </div>



                    <div className="sub_total">
                      <p className="m-0">Mehrwertsteuer  ({formatTaxRate(selectedPlan)})</p>
                      <p className="m-0">{formatPriceValue(selectedPlan?.tax_amount)}</p>
                    </div>
                  <div className="sub_total">
                    <p className="m-0">Gesamtpreis (inkl. MwSt.)</p>
                    <p className="m-0">{formatPlanPrice(selectedPlan)}</p>
                  </div>
                  {(() => {
                    const activeDiscount = getActiveDiscount();
                    if (!activeDiscount) return null;
                    const gross = getGrossValue(selectedPlan);
                    const discountedGross = getDiscountedGrossValue(gross, activeDiscount);
                    const durationLabel = getDiscountDurationLabel(activeDiscount, selectedPlan);
                    const discountValue = formatDiscountValue(activeDiscount);
                    const showMonths = activeDiscount.duration_type === "months" && activeDiscount.duration_months;
                    const showOnce = activeDiscount.duration_type === "once";
                    const isYearly = selectedPlan?.interval === "year";
                    return (
                      <>
                        <div className="sub_total mt-2">
                          <p className="m-0">
                            {durationLabel}
                            {discountValue ? ` (${discountValue})` : ""}
                          </p>
                          <p className="m-0">
                            {discountedGross === null ? "-" : formatCurrency(discountedGross)}
                          </p>
                        </div>
                        {showMonths && (
                          <div className="sub_total">
                            <p className="m-0">
                              {isYearly ? "Ab dem 2. Abrechnungsjahr" : `Ab Monat ${activeDiscount.duration_months! + 1}`}
                            </p>
                            <p className="m-0">{formatPlanPrice(selectedPlan)}</p>
                          </div>
                        )}
                        {showOnce && (
                          <div className="sub_total">
                            <p className="m-0">{isYearly ? "Ab dem 2. Abrechnungsjahr" : "Ab dem 2. Monat"}</p>
                            <p className="m-0">{formatPlanPrice(selectedPlan)}</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  <div>
                    {!autoDiscount && (
                      <div className="my-6">
                        <button
                          type="button"
                          onClick={() => setShowDiscountInput((prev) => !prev)}
                          className="text-[#32768d] !text-[12.5px] mb-2 font-medium hover:underline text-left w-full"
                        >
                          Promotion-Code hinzufügen
                        </button>
                        {showDiscountInput && (
                          <>
                            <div className="border p-3 rounded flex items-center gap-3">
                              <input
                                type="text"
                                className="w-full bg-transparent outline-none text-[#32325d] text-[16px] placeholder-[#aab7c4]"
                                value={discountCode}
                                onChange={(e) => setDiscountCode(e.target.value)}
                                placeholder="Code eingeben"
                                autoComplete="off"
                                style={{ fontFamily: "Arial, sans-serif" }}
                              />
                              <button
                                type="button"
                                onClick={handleDiscountApply}
                                className="ml-auto text-sm font-medium text-primary-600 hover:text-primary-700"
                              >
                                Anwenden
                              </button>
                            </div>
                            {discountError && (
                              <p className="mt-2 text-sm text-red-600">{discountError}</p>
                            )}
                            {appliedDiscount && !discountError && (
                              <p className="mt-2 text-sm text-green-600 font-medium">
                                Rabattcode angewendet: {appliedDiscount.code}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {autoDiscount && (
                      <div className="my-6 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
                        Automatischer Rabatt aktiv{autoDiscount.code ? `: ${autoDiscount.code}` : "."}
                      </div>
                    )}
                  </div>
                </div>
                  <div className="payment__portal__create__total">
                    {(() => {
                      const { isUpgrade, isDowngrade, periodEndLabel } = getPlanChange(selectedPlan);
                      if (isUpgrade) {
                        return (
                          <>
                            <p className="text-sm text-gray-700 mb-2">
                              Der neue Tarif gilt ab sofort.
                           
                              Die Abrechnung erfolgt anteilig für den laufenden Abrechnungszeitraum.
                          
                              Bereits gezahlte Beträge werden automatisch verrechnet.
                            </p>
                          </>
                        );
                      }
                      if (isDowngrade) {
                        return (
                          <>
                            <p className="text-sm text-gray-700 mb-2">
                              Der neue Tarif startet {periodEndLabel ? `am ${periodEndLabel}` : "zum Ende der aktuellen Abrechnungsperiode"}.
                              {" "}Ihr aktueller Tarif bleibt bis dahin unverändert.
                              {" "}Heute erfolgt keine Abbuchung.
                            </p>
                          </>
                        );
                      }
                      const previewAmount = formatPreviewAmount();
                      const activeDiscount = getActiveDiscount();
                      const gross = getGrossValue(selectedPlan);
                      const discountedGross = getDiscountedGrossValue(gross, activeDiscount);
                      return (
                        <>
                          <p>Gesamtbetrag heute fällig</p>
                          <p>
                            {previewAmount ??
                              (activeDiscount && discountedGross !== null
                                ? formatCurrency(discountedGross)
                                : formatPlanPrice(selectedPlan))}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    {!showCheckoutForm && (
                      <button
                        type="button"
                        id=""

                        className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !font-normal"
                        style={{
                          backgroundColor: "#32768d",
                          color: "white",
                          border: "none"
                        }}
                        onClick={() => {
                          setCheckoutStatus("idle");
                          setCheckoutMessage("");
                          setShowCheckoutForm(true);
                        }}
                      >
                        <div className="_btnFlex_1uoax_32">Weiter zur Kasse</div>
                      </button>
                    )}
                    <div className="flex flex-col gap-2 pt-3 w-full">
                      <div className="text-[#20A961] gap-1 flex items-center justify-center w-full">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="Frame"><path id="Vector" fillRule="evenodd" clipRule="evenodd" d="M8 1C7.07174 1 6.1815 1.36875 5.52513 2.02513C4.86875 2.6815 4.5 3.57174 4.5 4.5V6.5C3.96957 6.5 3.46086 6.71071 3.08579 7.08579C2.71071 7.46086 2.5 7.96957 2.5 8.5V13C2.5 13.5304 2.71071 14.0391 3.08579 14.4142C3.46086 14.7893 3.96957 15 4.5 15H11.5C12.0304 15 12.5391 14.7893 12.9142 14.4142C13.2893 14.0391 13.5 13.5304 13.5 13V8.5C13.5 7.96957 13.2893 7.46086 12.9142 7.08579C12.5391 6.71071 12.0304 6.5 11.5 6.5V4.5C11.5 2.56667 9.93333 1 8 1ZM10.5 6.5V4.5C10.5 3.83696 10.2366 3.20107 9.76777 2.73223C9.29893 2.26339 8.66304 2 8 2C7.33696 2 6.70107 2.26339 6.23223 2.73223C5.76339 3.20107 5.5 3.83696 5.5 4.5V6.5H10.5Z" fill="#20A961"></path></g></svg>
                        <span className="font-bold text-base"> Sicherer Checkout</span>
                      </div>
                      <div className="flex items-center justify-center w-full">
                        <p className="text-black m-0">
                          <span className="-mr-2 text-md font-normal">Powered by</span>
<svg fill="#0e0e0e" fillRule="evenodd" height="36" width="72" viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg">
                    
                    <path d="m101.547 30.94c0-5.885-2.85-10.53-8.3-10.53-5.47 0-8.782 4.644-8.782 10.483 0 6.92 3.908 10.414 9.517 10.414 2.736 0 4.805-.62 6.368-1.494v-4.598c-1.563.782-3.356 1.264-5.632 1.264-2.23 0-4.207-.782-4.46-3.494h11.24c0-.3.046-1.494.046-2.046zm-11.347-2.183c0-2.598 1.586-3.678 3.035-3.678 1.402 0 2.897 1.08 2.897 3.678zm-14.597-8.345c-2.253 0-3.7 1.057-4.506 1.793l-.3-1.425h-5.067v26.805l5.747-1.218.023-6.506c.828.598 2.046 1.448 4.07 1.448 4.115 0 7.862-3.3 7.862-10.598-.023-6.667-3.816-10.3-7.84-10.3zm-1.38 15.84c-1.356 0-2.16-.483-2.713-1.08l-.023-8.53c.598-.667 1.425-1.126 2.736-1.126 2.092 0 3.54 2.345 3.54 5.356 0 3.08-1.425 5.38-3.54 5.38zm-16.4-17.196 5.77-1.24v-4.666l-5.77 1.218zm0 1.747h5.77v20.115h-5.77zm-6.185 1.7-.368-1.7h-4.966v20.117h5.747v-13.634c1.356-1.77 3.655-1.448 4.368-1.195v-5.287c-.736-.276-3.425-.782-4.782 1.7zm-11.494-6.7-5.609 1.197-.023 18.414c0 3.402 2.552 5.908 5.954 5.908 1.885 0 3.264-.345 4.023-.76v-4.667c-.736.3-4.368 1.356-4.368-2.046v-8.149h4.368v-4.897h-4.37zm-15.54 10.828c0-.897.736-1.24 1.954-1.24a12.85 12.85 0 0 1 5.7 1.47v-5.391c-1.908-.76-3.793-1.057-5.7-1.057-4.667 0-7.77 2.437-7.77 6.506 0 6.345 8.736 5.333 8.736 8.07 0 1.057-.92 1.402-2.207 1.402-1.908 0-4.345-.782-6.276-1.84v5.47c2.138.92 4.3 1.3 6.276 1.3 4.782 0 8.07-2.368 8.07-6.483-.023-6.85-8.782-5.632-8.782-8.207z" />
                  </svg>                          <span className="font-normal"> - </span>
                          <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="" target="_blank" rel="noreferrer">Nutzungsbedingungen</a></span>
                          <span className="font-normal"> &amp; </span>
                          <span className="font-normal underline text-primary-500"><a className="font-normal text-sm" href="" target="_blank" rel="noreferrer">Datenschutz</a></span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )
            ) : (

              <>
                <div className="_tabList_yedxm_1 tab-list" role="tablist" aria-orientation="horizontal" style={{ outline: "none", marginBottom: "5px", background: "white", border: "1px solid rgb(235, 235, 235)", height: "42px", width: "900px" }} tabIndex={0} data-orientation="horizontal">
                  
                  <button
                    type="button"
                    role="tab"
                    aria-selected={selectedInterval === "month"}
                    aria-controls="radix-566-content-month"
                    data-state={selectedInterval === "month" ? "active" : "inactive"}
                    id="radix-566-trigger-month"
                    className="_tabButton_yedxm_16 tab-btn"
                    tabIndex={-1}
                    data-orientation="horizontal"
                    data-radix-collection-item=""
                    onClick={() => handleIntervalToggle("month")}
                  >
                    <span className="tab-btn_label">Monatliche Abrechung</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={selectedInterval === "year"}
                    aria-controls="radix-566-content-year"
                    data-state={selectedInterval === "year" ? "active" : "inactive"}
                    id="radix-566-trigger-year"
                    className="_tabButton_yedxm_16 tab-btn"
                    tabIndex={-1}
                    data-orientation="horizontal"
                    data-radix-collection-item=""
                    onClick={() => handleIntervalToggle("year")}
                  >
                    <span className="tab-btn_label flex items-center gap-2 justify-center flex-col sm:flex-row">
                      <span>Jährliche Abrechung</span>
                      <span className="rounded-md bg-green-200  px-2 py-1 text-black text-xs">17 % sparen</span>
                    </span>
                  </button>
                </div>
                <div ref={plansRef} className="flex flex-col md:flex-row justify-center gap-2 scroll-smooth">
                  {plans.filter((plan) => (selectedInterval === "year" ? plan.interval === "year" : plan.interval === "month")).map((plan, index) => {
                  const baseColor = plan.color ?? "#32768d";
                  const hoverColor = shadeColor(baseColor, -15);
                  const icon = iconMap[plan.icon_name] || <StarIcon fontSize="small" className="text-white" />;
                  const isRecommended = plan.name === "Basis Paket";
                  // Neue Logik für Planstatus
                  const { isCurrentPlan, isUpgrade, isDowngrade, isPendingPlan, periodEndLabel } = getPlanChange(plan);
                  const isDisabled = isCurrentPlan || isPendingPlan;
                  const actionLabel = isCurrentPlan
                    ? "Aktueller Plan"
                    : isPendingPlan
                      ? "Wechsel geplant"
                    : isDowngrade
                      ? (periodEndLabel ? `Wechseln ab ${periodEndLabel}` : "Zum Periodenende wechseln")
                      : isUpgrade
                        ? "Tarif jetzt erweitern"
                        : "Buchen";
                  // Dynamische Border-Farbe
                  return (
                    <div
                      key={index}
                      className={`relative w-[18rem] h-full rounded-2xl shadow-lg p-2 bg-white${isDisabled ? " opacity-70 cursor-not-allowed" : ""}`}
                    >
                      {/* Runde Bögen als Rahmen */}

                      {/* Inhalt mit Abstand damit Bögen sichtbar bleiben */}
                      <div
                        className={`relative z-10 bg-white rounded-2xl p-6 pt-8 pb-8 border-2 border-b-0`}
                        style={{ borderColor: plan.color }}
                      >
                        {isRecommended && (
                          <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                            Empfohlen
                          </div>
                        )}
                        <div style={{ backgroundColor: plan.color }} className="p-2 rounded-full mb-4 mx-auto w-fit">
                          <div className="w-6 h-6 flex items-center justify-center">{icon}</div>
                        </div>
                        <h3 className="text-xl font-bold text-center mb-1">{plan.name}</h3>
                        {plan.headline && (
                          <p style={{ color: plan.color }} className="font-semibold text-center mb-4">
                            {plan.headline}
                          </p>
                        )}
                        <p className="text-sm text-gray-700 text-center mb-4">{plan.description}</p>
                        <ul className="text-left text-sm text-gray-600 mb-4 space-y-2 pl-1">
                          {plan.features.map((feature, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 pr-2 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:outline-none hover:shadow-none transition-none"
                            >
                              <div style={{ backgroundColor: plan.color }} className="w-5 h-5 rounded-full flex items-center justify-center mt-0.5 shrink-0">
                                <svg
                                  className="w-3 h-3 text-white"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="text-center mb-3">
                          <p className="text-sm text-gray-700 mb-1">nur</p>
                          <p className="text-xl font-bold mb-0">
                            {formatCurrency(plan.price_gross_eur ?? plan.price_eur)}
                            <span className="text-base font-normal">{plan.interval === "year" ? "/ Jahr" : "/ Monat"}</span>
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">inkl. MwSt.</p>
                        </div>
                        <div className="flex flex-col space-y-2 items-center mt-4">
                          <div className="flex items-center space-x-2">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3" />
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                            <span className="text-gray-500 text-sm">Sichere Zahlung mit Stripe</span>
                          </div>
                          <div className="bg-gray-100 rounded-lg px-4 py-2 flex flex-col items-center w-full">
                            <div className="text-xs text-gray-600 mb-3 text-center leading-snug">
                              {plan.interval === "year" ? (
                                <>
                                  <p className="m-0">12 Monate Laufzeit</p>
                                  <p className="m-0">Jährlich kündbar</p>
                                </>
                              ) : (
                                <>
                                  <p className="m-0">Monatlich kündbar</p>
                                  <p className="m-0">Keine Mindestlaufzeit</p>
                                </>
                              )}
                            </div>
                            {isDisabled ? (
                              <button
                                type="button"
                                disabled
                                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 opacity-60 cursor-not-allowed"
                                style={{ backgroundColor: plan.color ?? "#32768d" }}
                              >
                                <div className="_btnFlex_19rto_32">{actionLabel}</div>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handlePlanSelect(plan)}
                                className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80"
                                style={{ backgroundColor: plan.color ?? "#32768d" }}
                              >
                                <div className="_btnFlex_19rto_32">
                                  {actionLabel}
                                </div>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </>
            )}
          </div>
        </div>)}
      {/* Modal für Kündigungsbestätigung */}
      {showCancelDialog && (
        <ModalWrapper
          onClose={() => {
            setShowCancelDialog(false);
            setCancelConfirmed(false);
          }}
          title="Bestätigung der Kündigung Ihres Abos"
        >
          <div>
            <p className="text-sm leading-[24px] text-gray-500 text-center">
              Wenn Sie Ihr Abonnement kündigen, bleibt es bis zum Ende des aktuellen Abrechnungszeitraums aktiv. Danach wird es automatisch beendet. Dieser Vorgang kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex grow md:grow-0 flex-col mb-4">
              <label className="inline-flex space-x-3 cursor-pointer text-xs sm:text-sm lg:text-base my-5 items-start">
                <input
                  required
                  className="h-4 w-4 border border-gray-300 rounded cursor-pointer checked:bg-[#40937a]"
                  type="checkbox"
                  value="agree"
                  checked={cancelConfirmed}
                  onChange={() => setCancelConfirmed(!cancelConfirmed)}
                />
                <p className="-m-1 text-gray-500 text-sm">
                  Ich bestätige, dass ich mein Abonnement kündigen möchte.
                </p>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                disabled={!cancelConfirmed}
                onClick={handleCancelSubscription}
                className={`inline-flex w-full md:w-fit px-3 py-2 ml-auto rounded shadow space-x-2 border-0 items-center justify-center text-left ${!cancelConfirmed
                  ? 'bg-danger-500/60 text-white cursor-not-allowed'
                  : 'bg-danger-500 text-white cursor-pointer'
                  }`}
              >
                Abo kündigen
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default PlanPicker;
